import * as domUtils from '../util/dom';
import videoAttributes from '../util/video-attributes';
import request from '../util/request';
import device from '../util/device';
import Sizzle from 'sizzle';
import debugUtil from '../util/debug';

const Debug = debugUtil('captions');
const EVENT_NAMESPACE = 'captions';

const fullscreenEvents = 'fullscreenchange, mozfullscreenchange, MSFullscreenChange, webkitfullscreenchange';

const CLASSNAMES = {
    captionsText: 'svp_js-captions-text',
    captionsContainer: 'svp_js-captions-container'
};

const STYLE_CLASSES = {
    captionsText: 'svp_captions-text',
    captionsContainer: 'svp_captions-container',
    captionsLine: 'svp_captions-line',
    elevated: 'svp_captions--elevated',
    visible: 'svp_captions-container--visible',
    align: {
        start: 'svp_captions-text--align-start',
        middle: 'svp_captions-text--align-middle',
        end: 'svp_captions-text--align-end'
    },
    position: 'svp_captions-text--position-config',
    vttline: 'svp_captions-text--line-config',
    bold: 'svp_captions-text--bold',
    italic: 'svp_captions-text--italic',
    underline: 'svp_captions-text--underline'
};

function getCurrentInstanceNamespace(api) {
    // Note that it is not possible to use api.video() here because that is not
    // available before video-load.
    const videoElement = Sizzle('video', api.element())[0];
    const uid = videoAttributes.getUID(videoElement) || videoAttributes.generateAndSetUID(videoElement);

    return `${EVENT_NAMESPACE}-${uid}`;
}

function createCaptionsContainerElement() {
    const captionsContainer = document.createElement('div');
    captionsContainer.className = `${CLASSNAMES.captionsContainer} ${STYLE_CLASSES.captionsContainer}`;

    return captionsContainer;
}

function createCaptionsTextElement() {
    const captionsText = document.createElement('div');
    captionsText.className = `${CLASSNAMES.captionsText} ${STYLE_CLASSES.captionsText}`;

    return captionsText;
}

function _getWebVttUrl(metadataObject) {
    const textRefs = metadataObject.subtitleReferences || [];
    let textUrl = false;

    textRefs.forEach((item) => {
        if (item.format === 'webvtt') {
            textUrl = item.url;
        }
    });

    return textUrl;
}

function complexTimeToSeconds(timeString /* hh:mm:ss.sss */) {
    return timeString
                .split(':')
                .map(parseFloat)
                .reduce((prev, current, index) => (
                    prev + (current * [3600, 60, 1][index])
                ), 0);
}


function formatText(line) {
    const innerHTML = line
        .replace(/(\r|\n)$/g, ' $&')
        .replace(/<b>/g, `<span class="${STYLE_CLASSES.bold}">`)
        .replace(/<i>/g, `<span class="${STYLE_CLASSES.italic}">`)
        .replace(/<u>/g, `<span class="${STYLE_CLASSES.underline}">`)
        .replace(/<c\.(.*?)>/g, '<span class="svp_captions-text--color-$1">')
        .replace(/<\/b>|<\/i>|<\/u>|<\/c>/g, '</span>');

    return `<span class="${STYLE_CLASSES.captionsLine}">${innerHTML}</span>`;
}

function getSegmentConfiguration(configTuples) {
    const config = {};

    configTuples.forEach((item) => {
        const keyVal = item.split(/:/);

        config[keyVal[0]] = keyVal[1];
    });

    return config;
}

function parseVttFile(data) {
    // remove first three lines of meta data
    const lines = data.trim().split("\n").slice(2);

    // Initialise with empty caption
    const captions = [];

    let segment = { id : '', innerHTML : ''};
    let inSegment;

    lines.forEach((line) => {
        if (/^\d+$/.test(line)) {
            segment.id = line;
        } else if (line.trim().length === 0 && inSegment) {
            captions.push(segment);
            segment = { start: 0, end: 0, innerHTML: '' };
            inSegment = false;
        } else {
            if (/-->/.test(line)) {
                // Format is of style 00:03:28.560 --> 00:03:31.160 align:end
                const time = line.split(/\s/);
                segment.start = complexTimeToSeconds(time[0]);
                segment.end = complexTimeToSeconds(time[2]);
                segment.config = time.length > 3 ? getSegmentConfiguration(time.slice(3)) : {};
                inSegment = true;
            } else if (inSegment) {
                if (segment.innerHTML !== '') {
                    segment.innerHTML += '<br>';
                }
                segment.innerHTML += formatText(line);
            }
        }
    });

    if (segment.start !== 0 && segment.end !== 0) {
        captions.push(segment);
    }

    Debug('Parsed VTT-file');

    return captions;
}

function _getConfigHtmlStyle(config) {
    const classes = [];
    const style = {};

    if (config.align && STYLE_CLASSES.align[config.align]) {
        classes.push(STYLE_CLASSES.align[config.align]);
    }

    if (config.position && config.position.match(/\d+%/)) {
        classes.push(STYLE_CLASSES.position);
        style.left = config.position;
    }

    if (config.line !== undefined && config.line.match(/%/)) { // TODO: support non-percentage values?
        classes.push(STYLE_CLASSES.vttline);
        style.top = config.line;
    }

    return { classes, style };
}

function isCaptionActive(caption, currentTime) {
    return currentTime >= caption.start && currentTime <= caption.end;
}

function _createCustomTrackElement(api, responseText) {
    if (!api.element()) {
        return; //happens if we try to play dash after flash and gets loadPlayerError and destroy SVP
    }

    const element = api.element();
    const captions = parseVttFile(responseText);
    const captionsContainerElement = createCaptionsContainerElement();

    let currCaptionsIndex = 0;
    let activeCaptionIndices = [];
    let enabled = false;
    let elevated = false;
    let fontSize = 15;
    let marginLeftRight = 10;

    const updateSizes = () => {
        fontSize = Math.max(element.offsetWidth * 0.0375, 15);
        marginLeftRight = element.offsetWidth * 0.175;
    };

    const renderCaption = (captionIndex, caption) => {
        // Is this caption already rendered?
        for (let i = 0; i < captionsContainerElement.children.length; i++) {
            const child = captionsContainerElement.children[i];
            if (child.getAttribute('data-caption-index') === captionIndex.toString()) {
                // Font size and margin might have changed because of window resize.
                child.style.fontSize = `${fontSize}px`;
                child.style.marginLeft = `${marginLeftRight}px`;
                child.style.marginRight = `${marginLeftRight}px`;

                return;
            }
        }

        const captionsTextElement = createCaptionsTextElement();
        captionsTextElement.setAttribute('data-caption-index', captionIndex);
        captionsTextElement.innerHTML = caption.innerHTML;

        const { style, classes } = _getConfigHtmlStyle(caption.config);
        Object.assign(captionsTextElement.style, style);
        captionsTextElement.className += ' ' + classes.join(' ');
        captionsTextElement.style.fontSize = `${fontSize}px`;
        captionsTextElement.style.marginLeft = `${marginLeftRight}px`;
        captionsTextElement.style.marginRight = `${marginLeftRight}px`;

        captionsContainerElement.appendChild(captionsTextElement);
    };

    const render = () => {
        if (enabled) {
            domUtils.toggleClass(captionsContainerElement, STYLE_CLASSES.elevated, elevated);
            domUtils.addClass(captionsContainerElement, STYLE_CLASSES.visible);
            activeCaptionIndices.forEach((index) => renderCaption(index, captions[index]));
        } else {
            domUtils.removeClass(captionsContainerElement, STYLE_CLASSES.visible);
        }
    };

    api.on(EVENT_NAMESPACE + '.timeupdate', function () {
        if (api.data().isRemote) {
            return;
        }

        if (enabled) {
            const currentTime = api.getCurrentTime();

            while (currCaptionsIndex + 1 < captions.length && currentTime > captions[currCaptionsIndex].end) {
                currCaptionsIndex++;
            }

            activeCaptionIndices.length = 0;
            for (let i = currCaptionsIndex; i < captions.length && isCaptionActive(captions[i], currentTime); i++) {
                activeCaptionIndices.push(i);
            }

            // Remove old caption texts
            for (let i = 0; i < captionsContainerElement.children.length; i++) {
                const child = captionsContainerElement.children[i];
                const captionIndex = parseInt(child.getAttribute('data-caption-index'), 10);

                if (activeCaptionIndices.indexOf(captionIndex) === -1) {
                    captionsContainerElement.removeChild(child);
                    i--;
                }
            }

            render();
        }
    });

    api.on(EVENT_NAMESPACE + '.seeked', function () {
        const currTime = api.getCurrentTime();
        for (let i = 0; i < captions.length; i++) {
            if (captions[i].end >= currTime) {
                currCaptionsIndex = i;
                break;
            }
        }

        render();
    });

    api.on(EVENT_NAMESPACE + '.captionsvisible', function (event) {
        enabled = event.detail.shouldShow;
        render();
    });

    api.on(EVENT_NAMESPACE + '.controls', function (event) {
        elevated = event.detail.visible;
        render();
    });

    const onResize = () => {
        updateSizes();
        render();
    };

    const currentInstanceNamespace = getCurrentInstanceNamespace(api);
    domUtils.addEvents(window, 'resize', onResize, currentInstanceNamespace);
    domUtils.addEvents(document, fullscreenEvents, onResize, currentInstanceNamespace);
    updateSizes();

    // TODO: Filters can not be removed. This will return the wrong value if we change
    // to a video without captions!
    api.filters.push({
        getTextTracks: () => {
            return [{ id: 'sv' }];
        }
    });

    api.element().appendChild(captionsContainerElement);
    api.fire('captionsloaded');
}

function _createTrackElement(api, textTrackUrl) {
    const videoElement = Sizzle('video', api.element())[0];

    api.off(EVENT_NAMESPACE + '.loadedmetadata');

    if (videoElement) {
        videoElement.setAttribute('crossorigin', 'anonymous');

        const track = document.createElement('track');
        track.kind = 'captions';
        track.label = 'Svenska';
        track.srclang = 'sv';
        track.src = textTrackUrl;

        videoElement.appendChild(track);

        api.on(EVENT_NAMESPACE + '.captionsvisible', function (event) {
            const { shouldShow } = event.detail;
            videoElement.textTracks[0].mode = shouldShow ? 'showing' : 'hidden';
        });

        api.fire('captionsloaded');
    }
}

function _reset(api) {
    api.off(EVENT_NAMESPACE + '.captionsvisible');
    api.off(EVENT_NAMESPACE + '.timeupdate');
    api.off(EVENT_NAMESPACE + '.seeked');
    api.off(EVENT_NAMESPACE + '.playing');
    api.off(EVENT_NAMESPACE + '.controls');
    api.off('track.controls');

    const currentInstanceNamespace = getCurrentInstanceNamespace(api);
    domUtils.removeEvents(window, 'resize', currentInstanceNamespace);
    domUtils.removeEvents(document, fullscreenEvents, currentInstanceNamespace);

    const element = Sizzle('.' + CLASSNAMES.captionsContainer, api.element())[0];
    if (element) {
        element.parentElement.removeChild(element);
    }
}

export function create(api) {
    api.on(EVENT_NAMESPACE + '.metadata', function (metdataEvent) {
        Debug('metadata received');
        const trackUrl = _getWebVttUrl(metdataEvent.detail);
        if (trackUrl) {
            if (device.isIOS()) {
                api.on(EVENT_NAMESPACE + '.loadedmetadata', _createTrackElement.bind(this, api, trackUrl));
            } else {
                request.getText(trackUrl, _createCustomTrackElement.bind(this, api));
            }
        }
    });

    api.filters.push({
        load: function () {
            _reset(api);
        }
    });
}

export function destroy(api) {
    _reset(api);
    api.off(EVENT_NAMESPACE + '.metadata');
}




// WEBPACK FOOTER //
// ./src/js/plugins/captions.js